import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Camera, Clapperboard, Copy, Download, Loader2, Music2, CheckCircle2, RefreshCw, Clock, ImageIcon, Quote, Info,
} from "lucide-react";
import { api, apiError, BACKEND_URL, downloadFile } from "../../lib/api";

const FORMATS = {
  foto: { label: "Foto con tu avatar", icon: ImageIcon, cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  video: { label: "Video hablando a cámara", icon: Clapperboard, cls: "bg-[#FACC15]/15 text-[#FACC15] border-[#FACC15]/40" },
  frase: { label: "Imagen de frase", icon: Quote, cls: "bg-purple-500/15 text-purple-300 border-purple-500/40" },
};

const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" });

const TikTokPostCard = ({ p, onPublished }) => {
  const f = FORMATS[p.format];
  const fullText = `${p.text}\n\n📩 ${p.md}`;

  const publish = async () => {
    try { await navigator.clipboard.writeText(fullText); } catch (err) { console.warn("clipboard:", err); }
    if (p.format === "video" && p.video_url) downloadFile(p.video_url, `tiktok-dia${p.day}.mp4`);
    else if (p.image_url) downloadFile(`${BACKEND_URL}${p.image_url}`, `tiktok-dia${p.day}.jpg`);
    window.open("https://www.tiktok.com/upload", "_blank");
    toast.success("Texto copiado y archivo descargado. Sube el archivo en TikTok, pega tu texto y publica.");
    try { await api.post(`/renalife/tiktok/posts/${p.post_id}/published`); onPublished(p.post_id); } catch (err) { console.warn(err); }
  };

  const copyText = async () => {
    await navigator.clipboard.writeText(fullText);
    toast.success("Texto del día copiado");
  };

  return (
    <div data-testid={`tiktok-post-${p.day}`} className={`rounded-xl border p-4 ${p.status === "published" ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/10 bg-black/30"}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="font-renalife font-bold text-[#FACC15]">Día {p.day}</span>
        <span className="text-xs text-zinc-400 capitalize">{fmtDate(p.date)}</span>
        <span className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full border px-2.5 py-1 ${f.cls}`}>
          <f.icon size={12} /> {f.label}
        </span>
        {p.status === "published" && (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400 font-bold ml-auto"><CheckCircle2 size={13} /> Publicada</span>
        )}
      </div>
      {p.tip && (
        <div data-testid="tiktok-dm-tip" className="flex items-start gap-2 rounded-lg border border-[#FACC15]/30 bg-[#FACC15]/10 px-3 py-2.5 mb-3 text-[12px] text-yellow-100/90">
          <Info size={14} className="text-[#FACC15] mt-0.5 shrink-0" /> {p.tip}
        </div>
      )}
      <div className="grid sm:grid-cols-[150px_1fr] gap-4">
        <div>
          {p.status === "pending" || p.status === "processing" ? (
            <div className="rounded-lg border border-dashed border-white/15 aspect-[9/16] flex flex-col items-center justify-center text-[11px] text-zinc-500 gap-2 p-2 text-center">
              <Loader2 size={16} className="animate-spin text-[#FACC15]" />
              {p.format === "video" ? "Generando tu video…" : "Generando tu imagen…"}
            </div>
          ) : p.status === "error" ? (
            <div className="rounded-lg border border-red-500/30 aspect-[9/16] flex items-center justify-center text-[11px] text-red-400 p-2 text-center">
              Falló la generación. Usa "Reanudar" arriba.
            </div>
          ) : p.format === "video" && p.video_url ? (
            <video src={p.video_url} controls className="rounded-lg border border-white/10 w-full bg-black" />
          ) : p.image_url ? (
            <img src={`${BACKEND_URL}${p.image_url}`} alt={`Día ${p.day}`} className="rounded-lg border border-white/10 w-full object-cover" />
          ) : null}
        </div>
        <div>
          <p className="text-[13px] text-zinc-200 whitespace-pre-wrap leading-relaxed">{p.text}</p>
          <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 px-2.5 py-1 font-semibold">
            📩 Cierre a DM: {p.md}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <button data-testid={`copy-tiktok-${p.day}`} onClick={copyText} className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-3 py-1.5 border border-white/15 text-zinc-300 hover:bg-white/5"><Copy size={12} /> Copiar texto</button>
            {(p.image_url || p.video_url) && (
              <button
                onClick={() => (p.format === "video" && p.video_url
                  ? downloadFile(p.video_url, `tiktok-dia${p.day}.mp4`)
                  : downloadFile(`${BACKEND_URL}${p.image_url}`, `tiktok-dia${p.day}.jpg`))}
                className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-3 py-1.5 border border-white/15 text-zinc-300 hover:bg-white/5"
              >
                <Download size={12} /> Descargar
              </button>
            )}
            <button data-testid={`publish-tiktok-${p.day}`} onClick={publish} className="inline-flex items-center gap-1 text-xs font-bold rounded-lg px-4 py-1.5 bg-white text-black hover:bg-zinc-200">
              <Music2 size={12} /> Publicar en TikTok
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function RenalifeTikTok() {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [starting, setStarting] = useState(false);
  const fileRef = useRef(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/renalife/tiktok");
      setData(d);
      if ((d.posts || []).some((p) => ["pending", "processing"].includes(p.status))) {
        clearTimeout(timer.current);
        timer.current = setTimeout(load, 7000);
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    load();
    return () => clearTimeout(timer.current);
  }, [load]);

  const uploadSelfie = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: up } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.info("Selfie recibida. La IA está creando tu avatar…");
      const { data: av } = await api.post("/renalife/tiktok/avatar", { file_id: up.file_id });
      setData((d) => ({ ...d, avatar_url: av.avatar_url }));
      toast.success("¡Tu avatar está listo!");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const start = async () => {
    setStarting(true);
    try {
      const { data: d } = await api.post("/renalife/tiktok/start");
      setData(d);
      toast.success("¡Serie de 15 días iniciada! La IA está generando tus fotos, frases y videos.");
      clearTimeout(timer.current);
      timer.current = setTimeout(load, 7000);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setStarting(false);
    }
  };

  const resume = async () => {
    try {
      await api.post("/renalife/tiktok/resume");
      toast.success("Reanudando la generación…");
      clearTimeout(timer.current);
      timer.current = setTimeout(load, 6000);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const markPublished = (postId) => {
    setData((d) => ({ ...d, posts: d.posts.map((p) => (p.post_id === postId ? { ...p, status: "published" } : p)) }));
  };

  if (!data) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#FACC15]" size={30} /></div>;
  }

  const posts = data.posts || [];
  const generating = posts.some((p) => ["pending", "processing"].includes(p.status));
  const errors = posts.some((p) => p.status === "error");

  return (
    <div data-testid="renalife-tiktok-page" className="space-y-8">
      <div>
        <h1 className="font-renalife font-bold text-3xl sm:text-4xl text-[#F8FAF8]">
          TikTok con tu <span className="text-[#FACC15]">Avatar IA</span>
        </h1>
        <p className="text-sm text-emerald-100/70 mt-2 max-w-2xl">
          Serie especial de 15 días para tu TikTok personal: fotos y videos donde apareces TÚ (con tu avatar creado por
          IA a partir de una selfie) hablando a la cámara, más imágenes de frase. Cada día termina invitando a que te
          escriban por mensaje directo (DM).
        </p>
      </div>

      {/* Recuadro explicativo */}
      <div data-testid="tiktok-explainer" className="rounded-2xl border border-[#FACC15]/25 p-6" style={{ background: "linear-gradient(135deg, #022C22, #064E3B)" }}>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FACC15]/70 mb-3">Cómo funciona</div>
        <ol className="space-y-2 text-[13px] text-emerald-100/85 list-decimal list-inside">
          <li><span className="font-semibold text-[#F8FAF8]">Tómate una selfie</span> con buena luz y súbela aquí: la IA crea tu avatar (tu rostro se conserva fiel) para que tus videos parezcan grabados con la cámara de tu propio celular.</li>
          <li><span className="font-semibold text-[#F8FAF8]">Presiona EMPEZAR PUBLICACIONES</span>: la IA prepara los 15 días — días 1, 4, 7, 10 y 13 foto con tu avatar; días 2, 5, 8, 11 y 14 video corto hablando a cámara; días 3, 6, 9, 12 y 15 imagen tipo frase.</li>
          <li><span className="font-semibold text-[#F8FAF8]">Publica 1 pieza al día</span> en alguno de los 3 horarios de alta actividad y re-úsala como historia en otro horario del mismo día.</li>
          <li><span className="font-semibold text-[#F8FAF8]">Siempre cierra invitando al DM</span>: cada texto ya termina con una invitación simple a escribirte por mensaje directo.</li>
        </ol>
        <div className="flex flex-wrap gap-2 mt-4">
          {(data.slots || []).map((s) => (
            <span key={s} className="inline-flex items-center gap-1 text-[11px] rounded-full border border-[#FACC15]/40 bg-[#FACC15]/10 text-[#FACC15] px-3 py-1.5 font-semibold">
              <Clock size={11} /> {s}
            </span>
          ))}
        </div>
      </div>

      {/* Avatar */}
      <div data-testid="avatar-section" className="rounded-2xl border border-white/10 bg-black/30 p-6">
        <h2 className="font-renalife font-bold text-xl text-[#F8FAF8] mb-1">Paso 1 · Tu avatar</h2>
        <p className="text-xs text-zinc-400 mb-4">Sube una selfie clara de tu rostro (de frente, buena luz, sin lentes oscuros).</p>
        <div className="flex flex-wrap items-center gap-5">
          {data.avatar_url ? (
            <img data-testid="avatar-preview" src={`${BACKEND_URL}${data.avatar_url}`} alt="Tu avatar" className="w-36 rounded-xl border border-[#FACC15]/40 object-cover" />
          ) : (
            <div className="w-36 aspect-[9/16] rounded-xl border border-dashed border-white/20 flex items-center justify-center text-zinc-600"><Camera size={26} /></div>
          )}
          <div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadSelfie(e.target.files?.[0])} />
            <button
              data-testid="upload-selfie-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-bold bg-[#FACC15] text-[#022C22] hover:bg-yellow-300 disabled:opacity-60"
            >
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
              {uploading ? "Creando tu avatar…" : data.avatar_url ? "Cambiar selfie" : "Subir mi selfie"}
            </button>
            <p className="text-[11px] text-zinc-500 mt-2 max-w-xs">Tu avatar se guarda y se usa en todas las fotos y videos de tu serie.</p>
          </div>
        </div>
      </div>

      {/* Empezar / progreso */}
      {!data.started ? (
        <div className="text-center py-6">
          <button
            data-testid="start-tiktok-btn"
            onClick={start}
            disabled={starting || !data.avatar_url}
            className="inline-flex items-center gap-3 rounded-full px-10 py-5 font-renalife font-bold text-xl bg-[#FACC15] text-[#022C22] hover:bg-yellow-300 transition-transform hover:-translate-y-0.5 disabled:opacity-40"
          >
            {starting ? <Loader2 size={22} className="animate-spin" /> : <Music2 size={22} />}
            EMPEZAR PUBLICACIONES
          </button>
          {!data.avatar_url && <p className="text-xs text-zinc-500 mt-3">Primero sube tu selfie para crear tu avatar.</p>}
        </div>
      ) : (
        <>
          <div data-testid="tiktok-progress" className="rounded-xl border border-white/10 bg-black/30 p-4 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="text-xs text-zinc-400 mb-1.5">
                {generating ? "La IA está generando tus piezas (los videos tardan varios minutos)…" : "Piezas listas"} · {data.ready}/{data.total}
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-[#FACC15] transition-all" style={{ width: `${(data.ready / Math.max(data.total, 1)) * 100}%` }} />
              </div>
            </div>
            {(generating || errors) && (
              <button data-testid="resume-tiktok-btn" onClick={resume} className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-2 border border-[#FACC15]/40 text-[#FACC15] hover:bg-[#FACC15]/10">
                <RefreshCw size={13} /> ¿Se detuvo? Reanudar
              </button>
            )}
          </div>
          <div className="space-y-4">
            {posts.map((p) => <TikTokPostCard key={p.post_id} p={p} onPublished={markPublished} />)}
          </div>
        </>
      )}
    </div>
  );
}
